import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { BaseButton, BaseInput } from "@wantable/Shared/Components";
import { useAuth } from "@wantable/Shared/Hooks";
import { ErrorIcon, SuccessIcon } from "@wantable/Shared/Icons";

import Divider from "./Divider";
import SocialMediaProviders from "./SocialMediaProviders";
import CloseButton from "../Shared/CloseButton";
import { validationSchema } from "./validator";
import useGoogleAuthentication from "../../hooks/useGoogleAuthentication";
import useFacebookAuthentication from "../../hooks/useFacebookAuthentication";
import { addEnterButtonListener } from "../../helpers/addEnterButtonListener";
import { ModalType }  from "../types";

interface ILoginFormProps {
  variantLabel: "login" | "signup" | "forgot";
  onClose: () => void;
  onSwitchModal: (modal: ModalType) => void;
  onAfterLogin?: () => void;
}

type LoginFormStatus = "error" | "success" | null;

const LoginForm = ({ variantLabel, onClose, onSwitchModal, onAfterLogin }: ILoginFormProps) => {
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginFormStatus, setLoginFormStatus] =
    useState<LoginFormStatus>(null);
  const [formMessage, setFormMessage] = useState("");
  const { loginWithGoogle } = useGoogleAuthentication();
  const { loginWithFacebook } = useFacebookAuthentication();
  const { login } = useAuth();

  if(!window.WANTABLE_header?.signInRedirect) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const checkoutUrl = urlParams.get('checkout_url')?.replace(/^.*\/\/[^\/]+/, '');
    window.WANTABLE_header.signInRedirect = checkoutUrl!;
  }

  const { values, setFieldValue, handleSubmit, errors, touched } = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validateOnBlur: true,
    validationSchema,
    onSubmit: async ({ email, password }) => {
      try {

        setLoggingIn(true);
        await login(email, password);

        if (onAfterLogin) {
          onAfterLogin();
        }

        onClose();
        setLoginFormStatus("success");
      } catch (error) {
        // @ts-ignore
        setFormMessage(error.message);
        setLoginFormStatus("error");
      } finally {
        setLoggingIn(false);
      }
    },
  });

  useEffect(() => {
    return addEnterButtonListener(handleSubmit);
  }, []);

  return (
    <form
      onClick={(event) => event.stopPropagation()}
      onSubmit={(event) => event.preventDefault()}
      className="w-full mx-[13px] mt-[20px] md:w-[440px] bg-white rounded-10 py-[30px] px-[15px] md:px-[56px] flex flex-col items-center cursor-auto relative"
    >
      <CloseButton onClick={onClose} />
      <h2 className="text-mobile-heading-3 md:text-desktop-heading-3 font-black font-primary">
        Welcome back
      </h2>
      <p className="mt-[12px] mb-[10px] text-mobile-body-lg font-secondary">
        Simply enter your details to begin
      </p>
      <div
        className={`w-full flex flex-row items-center justify-center text-mobile-body md:text-desktop-body ${
          loginFormStatus === "success" ? "text-[#25B900]" : "text-core-red"
        } font-medium font-secondary mb-[15px]`}
      >
        <ErrorIcon hidden={loginFormStatus !== "error"} className="pr-4" />
        {loginFormStatus === "success" && <SuccessIcon className="pr-4" />}
        {formMessage}
      </div>

      <fieldset className="w-full flex flex-col items-center gap-[10px]">
        <BaseInput
          type="text"
          placeholder="Enter email"
          label="Email Address"
          value={values.email}
          name="email"
          onChange={(value) => setFieldValue("email", value)}
          onBlur={() => {}}
          error={touched.email ? errors.email : undefined}
        />
        <BaseInput
          type="password"
          placeholder="Enter password"
          label="Password"
          value={values.password}
          name="password"
          onChange={(value) => setFieldValue("password", value)}
          onBlur={() => {}}
          error={touched.email ? errors.password : undefined}
        />

        <div className="w-full">
          <BaseButton
            mode="button"
            onClick={handleSubmit}
            fullWidth
            isSubmitButton
            disabled={loggingIn}
            showSpinner={loggingIn}
          >
            Submit
          </BaseButton>
        </div>
      </fieldset>

      <Divider />

      <SocialMediaProviders
        variant={variantLabel}
        onFacebookProvider={loginWithFacebook}
        onGoogleProvider={loginWithGoogle}
      />

      <div className="w-full flex items-center justify-center mt-30">
        <BaseButton mode="button" fullWidth variant="plainUnderline" onClick={() => onSwitchModal("signup")}>
          New to Wantable? Signup
        </BaseButton>
      </div>

      <div className="w-full flex items-center justify-center mt-30">
        <BaseButton mode="button" fullWidth variant="plainUnderline" onClick={() => onSwitchModal("forgot")}>
          Forgot your password?
        </BaseButton>
      </div>
    </form>
  );
};

export default LoginForm;
